import React from 'react';
import {useFieldArray, Controller, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {map, isEmpty} from 'lodash';
import {UNIT_PRICE_LABELS} from '../constants';
import {UNIT_PRICE_TYPES} from '../../../utils/upselling';
import {usePaymentSettings} from '../../../hooks';
import {ControlledCounter} from 'components/acommon/Counter';
import {PriceFormsProps} from './types';
import {FORM_NAMES, FormTypes, ItemsFields, ItemsFieldsNames} from './UpsellingOrderForm';
import Checkbox from 'components/Checkbox';
import {OrderRow, MultiPriceItem, PriceItemRow, OrderCol} from './styled';

function MultiPriceForm({offerData, disabled}: PriceFormsProps) {
  const {t} = useTranslation();
  const {paymentSettingsCurrencySign: currency} = usePaymentSettings();
  const {control, setValue, getValues} = useFormContext<
    FormTypes & Record<string, any>
  >();
  const {fields} = useFieldArray({
    name: FORM_NAMES.items,
  });

  React.useEffect(() => {
    if (!isEmpty(offerData.items) && !fields.length) {
      const items = offerData.items.map((item: any) => ({
        id: item.id,
        quantity: 0,
        name: item.name,
        price: item.price,
        unit_type: item.unit_type,
        checked: false,
      }));
      setValue(FORM_NAMES.items, items);
    }
  }, [fields.length, offerData.items, setValue]);

  const handleChangeQuantityForm =
    (checkedName: string, onChange: (value: number) => void) => (newValue: number) => {
      const isChecked = newValue > 0;
      setValue(checkedName, isChecked);
      onChange(newValue);
    };

  const handleClickCheckbox =
    (newValue: boolean, quantityName: string, onChange: (checked: boolean) => void) =>
    () => {
      const quantityNewValue = newValue ? 1 : 0;
      setValue(quantityName, quantityNewValue);
      onChange(newValue);
    };

  return (
    <div>
      <OrderRow style={{border: '0', padding: '0 0 0 40px'}}>
        <div className="rowName">{t('price')}</div>
      </OrderRow>

      <OrderCol>
        {map(
          fields,
          ({id, quantity, checked, name, price, unit_type, ...it}: ItemsFields, idx) => {
            const itemName = `${FORM_NAMES.items}.${idx}`;
            const checkedName = `${itemName}.${ItemsFieldsNames.checked}`;
            const quantityName = `${itemName}.${ItemsFieldsNames.quantity}`;
            const isChecked = getValues(checkedName);

            return (
              <MultiPriceItem key={id} isSelected={isChecked}>
                <PriceItemRow>
                  <div className="leftPriceItemPart">
                    <Controller
                      control={control}
                      name={checkedName}
                      render={({field: {ref, value, onChange, ...restField}}) => {
                        return (
                          <Checkbox
                            className="itemCheckbox"
                            checked={value}
                            disabled={disabled}
                            onChange={handleClickCheckbox(!value, quantityName, onChange)}
                            round
                            {...restField}
                          />
                        );
                      }}
                    />
                    <div className="itemName">{name}</div>
                  </div>

                  <div className="rightPriceItemPart">
                    <div className="itemCounter">
                      <Controller
                        control={control}
                        name={quantityName}
                        render={({field: {ref, onChange, ...restField}}) => {
                          return (
                            <ControlledCounter
                              disabled={disabled}
                              onChange={handleChangeQuantityForm(checkedName, onChange)}
                              {...restField}
                            />
                          );
                        }}
                        rules={{required: t('required') as string}}
                      />
                    </div>
                    <div className="rowValue">
                      <span className="newPrice">{`${price}${currency}`}&nbsp;</span>
                      <span className="unitType">
                        / {t(UNIT_PRICE_LABELS[unit_type || UNIT_PRICE_TYPES.unit])}
                      </span>
                    </div>
                  </div>
                </PriceItemRow>
              </MultiPriceItem>
            );
          },
        )}
      </OrderCol>
    </div>
  );
}

export default React.memo(MultiPriceForm);
